import React, { useState } from 'react';

import type { AiAction } from '~/components/AiChat/types';
import Text from '~/components/core/TextComponents/Text';
import ChevronDownIcon from '~/components/icons/ChevronDownIcon';
import ChevronUpIcon from '~/components/icons/ChevronUpIcon';
import colors from '~/theme/tailwind/colors';

interface ExplanationToggleProps {
  action: AiAction;
  /** The descriptor's inner component — the one the Clive Insights card renders. */
  InnerComponent: React.ElementType;
  isOpenByDefault: boolean;
}

/**
 * Why Clive proposed the action, behind a toggle: open while it is waiting, collapsed once settled.
 * The caller keys it on the state, so a card executed a moment after it was raised remounts
 * collapsed rather than keeping the toggle it was opened with.
 */
const ExplanationToggle: React.FC<ExplanationToggleProps> = ({ action, InnerComponent, isOpenByDefault }) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-fit cursor-pointer items-center gap-4 border-0 bg-transparent p-0 text-slate-800"
      >
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD}>
          {isOpen ? 'Why' : 'Why — show reasoning'}
        </Text>
        {isOpen ? (
          <ChevronUpIcon size={14} iconColor={colors.grey[700]} />
        ) : (
          <ChevronDownIcon size={14} iconColor={colors.grey[700]} />
        )}
      </button>
      {isOpen ? <InnerComponent action={action} /> : null}
    </div>
  );
};

export default ExplanationToggle;
